import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import Button from '../ui/Button';
import FollowUpCard from './FollowUpCard';
import FollowUpForm from './FollowUpForm';
import salesService from '../../services/sales';
import { errorMessage } from '../../utils/apiResponse';
import type { FollowUp, FollowUpFilter, FollowUpKind, SalesPerson } from '../../types/sales';

const FILTERS: { key: FollowUpFilter; label: string }[] = [
  { key: 'mine', label: 'Mine' },
  { key: 'open', label: 'Everyone\'s' },
  // Not "Done": every open card has a Done button.
  { key: 'done', label: 'Completed' },
];

/**
 * Follow-ups and issues across the organisation: mine, everyone's open, or
 * completed, soonest first, and tasks or issues only. `?show=`, `?kind=` and
 * `?item=` are in the URL, so a notification opens the right list with its
 * follow-up on top and its thread open, wherever that follow-up now is.
 * On /sales (the Follow-ups tab) and on /sales/follow-ups, which a Nursery
 * seat also opens.
 */
export default function FollowUpsBoard({ tz, onMineCount }: { tz: string; onMineCount?: (n: number) => void }) {
  const [params, setParams] = useSearchParams();
  const filter: FollowUpFilter = (['mine', 'open', 'done'] as const).find((f) => f === params.get('show')) ?? 'mine';
  const kind: FollowUpKind | null = (['task', 'issue'] as const).find((k) => k === params.get('kind')) ?? null;
  const itemId = Number(params.get('item')) || null;

  const [list, setList] = useState<FollowUp[] | null>(null);
  const [item, setItem] = useState<FollowUp | null>(null);
  const [people, setPeople] = useState<SalesPerson[]>([]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [res, mine] = await Promise.all([
        salesService.listFollowUps(filter, { kind }),
        filter === 'mine' && !kind ? null : salesService.listFollowUps('mine'),
      ]);
      if (!res.success) throw res;
      setList(res.data ?? []);
      const minePage = filter === 'mine' && !kind ? res : mine;
      if (minePage?.success) onMineCount?.((minePage.data ?? []).length);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the follow-ups'));
    }
  }, [filter, kind, onMineCount]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    salesService.listCrmPeople().then((r) => { if (r.success && r.data) setPeople(r.data); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!itemId) { setItem(null); return; }
    salesService.getFollowUp(itemId).then((r) => { if (r.success && r.data) setItem(r.data); }).catch(() => {});
  }, [itemId]);

  const setParam = (key: string, value: string | null) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value); else p.delete(key);
    p.delete('item');
    setParams(p, { replace: true });
  };

  const replace = (fu: FollowUp) => {
    if (item && fu.id === item.id) setItem(fu);
    setList((l) => {
      if (!l) return l;
      const fits = (filter === 'done' ? fu.status === 'done' : fu.status === 'open') && (!kind || fu.kind === kind);
      return fits ? l.map((x) => (x.id === fu.id ? fu : x)) : l.filter((x) => x.id !== fu.id);
    });
    load();
  };

  const shown = (list ?? []).filter((f) => f.id !== item?.id);

  return (
    <section className="bg-white border border-neutral-200 rounded-xl min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-neutral-100">
        <h2 className="text-base font-semibold text-neutral-900">Follow-ups and issues</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setParam('show', f.key)} aria-pressed={filter === f.key}
                className={`px-2 py-1 text-xs font-medium rounded-md ${filter === f.key ? 'bg-neutral-800 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <label className="sr-only" htmlFor="fu-kind">Show</label>
          <select id="fu-kind" value={kind ?? ''} onChange={(e) => setParam('kind', e.target.value || null)}
            className="rounded-md border border-neutral-300 px-2 py-1 text-xs">
            <option value="">Follow-ups and issues</option>
            <option value="task">Follow-ups only</option>
            <option value="issue">Issues only</option>
          </select>
          {!adding && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4 mr-1" aria-hidden />New
            </Button>
          )}
        </div>
      </div>
      {adding && (
        <div className="p-4 border-b border-neutral-100">
          <FollowUpForm people={people} tz={tz} defaultKind={kind ?? 'task'}
            onSaved={() => { setAdding(false); load(); }}
            onCancel={() => setAdding(false)} />
        </div>
      )}
      {error && <p className="p-4 text-sm text-red-700 break-words">{error}</p>}
      {item && (
        <ul className="border-b-4 border-primary-100" aria-label="Opened from a notification">
          <FollowUpCard key={`item-${item.id}`} fu={item} people={people} tz={tz} onChange={replace} startOpen />
        </ul>
      )}
      {list && shown.length === 0 && !item && (
        <p className="p-4 text-sm text-neutral-600">
          {filter === 'done' ? 'Nothing completed yet.' : filter === 'mine' ? 'Nothing for you to follow up.' : 'Nothing waiting to be followed up.'}
        </p>
      )}
      {shown.length > 0 && (
        <ul className="divide-y divide-neutral-100">
          {shown.map((f) => <FollowUpCard key={f.id} fu={f} people={people} tz={tz} onChange={replace} />)}
        </ul>
      )}
    </section>
  );
}
